import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { markActivity, isIdleExpired, clearActivity } from './sessionActivity';

const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'wheel', 'touchstart'];

// 1. إنشاء الـ Context بقيمة افتراضية null
const AuthContext = createContext(null);

// 🎯 فحص صلاحية التوكن محلياً (من غير أي نداء للسيرفر) بقراءة claim الـ exp
// جوه الـ JWT - ده اللي كان ناقص: initAuth تحت كانت بتثق في وجود "user" في
// localStorage بس من غير ما تتأكد إن التوكن نفسه لسه صالح، فالجلسة كانت
// بتفضل "شغالة" شكلياً في الواجهة حتى بعد ما التوكن الحقيقي ينتهي فعلياً
// (30 دقيقة بحسب ACCESS_TOKEN_EXPIRE_MINUTES في الباك إند)
const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const base64Payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(decodeURIComponent(escape(window.atob(base64Payload))));
    if (!payload.exp) return false;
    return Date.now() >= payload.exp * 1000;
  } catch (e) {
    return true; // توكن مش قابل للقراءة = اعتبره منتهي/غير صالح
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // 🎯 حالة معالج الإعداد الأول - محتاج إعداد يعني صفر مؤسسات في القاعدة
  // كلها. بنجيبها مرة واحدة بس أول ما اليوزر يبقى معروف، مش مربوطة
  // بأي state تاني عشان معالج الإعداد نفسه يقدر يصفّرها فورًا بعد ما يخلص
  const [needsSetup, setNeedsSetup] = useState(false);
  const [setupLoading, setSetupLoading] = useState(true);

  const refreshSetupStatus = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setSetupLoading(false);
      return;
    }
    try {
      const res = await axios.get('/api/system/setup-status', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNeedsSetup(!!res.data?.needs_setup);
    } catch (err) {
      // لو الفحص نفسه فشل، الأسلم إننا منمنعش الدخول للسيستم بسبب خطأ شبكة عابر
      console.error('Setup status check failed', err);
      setNeedsSetup(false);
    } finally {
      setSetupLoading(false);
    }
  }, []);

  useEffect(() => {
      const initAuth = () => {
        //console.log("🔍 [Debug] Checking LocalStorage for user...");
        try {
          const savedUser = localStorage.getItem('user');
          const savedToken = localStorage.getItem('token');
          //console.log("🔍 [Debug] Raw user data from storage:", savedUser);

          // 🎯 isIdleExpired هنا عشان ريفريش الصفحة بعد 30 دقيقة من غير نشاط يطلّع
          // المستخدم، بدل ما الـ Header يجدد التوكن اللي لسه ماخلصش
          if (savedUser && savedUser !== "undefined" && savedUser !== "null" && !isTokenExpired(savedToken) && !isIdleExpired()) {
            const parsedUser = JSON.parse(savedUser);
            //console.log("🔍 [Debug] Parsed User:", parsedUser);
            setUser(parsedUser);
          } else {
            // 🎯 لو التوكن منتهي (أو مفيش يوزر خالص)، نمسح أي بقايا جلسة قديمة
            // عشان الواجهة ما تفضلش شكلياً "مسجل دخول" وهي فعلياً مش كده
            if (savedUser) {
              localStorage.removeItem('user');
              localStorage.removeItem('token');
              clearActivity();
            }
            console.log("🔍 [Debug] No valid session found in storage.");
          }
        } catch (error) {
          console.error("❌ [Debug] JSON Parse Error:", error);
        } finally {
          //console.log("🔍 [Debug] Setting loading to false.");
          setLoading(false); // دي لازم تتنفذ عشان الكلمة تختفي
        }
      };
      initAuth();
  }, []);

  // 🎯 فحص دوري (كل 30 ثانية) لصلاحية التوكن حتى لو المستخدم سايب التاب مفتوح
  // من غير أي تفاعل - من غير الفحص ده، التاب كان بيفضل شكلياً "مسجل دخول"
  // للأبد لأن مفيش حاجة بتاخد بالها إن الوقت عدّى غير نداء API بيفشل فعلياً
  // + بعد 30 دقيقة من غير نشاط، أو لو تاب تاني عمل logout (التوكن اتمسح)
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => {
      const token = localStorage.getItem('token');
      if (!token || isTokenExpired(token) || isIdleExpired()) {
        setUser(null);
        setNeedsSetup(false);
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        clearActivity();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [user]);

  // 🎯 مزامنة الجلسة بين التابات: لو اتعمل login في تاب تاني، التاب ده (حتى لو
  // واقف على شاشة الدخول) يدخل على طول، ولو اتعمل logout هناك يخرج فورًا بدل
  // ما يستنى الفحص الدوري. الـ storage event بيوصل للتابات التانية بس، مش اللي
  // غيّرت القيمة. lastActivity برضو ضمن المفاتيح لأن الـ login بيكتبه بعد الـ user
  useEffect(() => {
    const SESSION_KEYS = ['token', 'user', 'lastActivity'];
    const onStorage = (e) => {
      if (e.key !== null && !SESSION_KEYS.includes(e.key)) return;
      const savedToken = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');
      if (!savedToken || !savedUser || savedUser === 'undefined' || savedUser === 'null') {
        setUser(null);
        setNeedsSetup(false);
        return;
      }
      if (isTokenExpired(savedToken) || isIdleExpired()) return; // الفحص الدوري بيتصرف
      try {
        setUser(prev => (prev && JSON.stringify(prev) === savedUser ? prev : JSON.parse(savedUser)));
      } catch (err) { /* user مش JSON سليم */ }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    if (!user) return;
    const onActivity = () => markActivity();
    ACTIVITY_EVENTS.forEach(evt => window.addEventListener(evt, onActivity, { passive: true }));
    return () => ACTIVITY_EVENTS.forEach(evt => window.removeEventListener(evt, onActivity));
  }, [user]);

  useEffect(() => {
    if (user) {
      refreshSetupStatus();
    } else {
      setSetupLoading(false);
    }
  }, [user, refreshSetupStatus]);

  const login = (userData) => {
    markActivity(true);
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const logout = () => {
    setUser(null);
    setNeedsSetup(false);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    clearActivity();
  };

  // 🎯 دالة فحص الصلاحيات مع حماية ضد الـ Undefined
  const hasPermission = (module, action = 'read') => {
    // 1. لو مفيش يوزر أو رول، اقفل الحنفية فوراً
    if (!user || !user.role) return false;
  
    // 2. استثناء الـ Super Admin - is_super_admin بيرجع صريح من الباك إند،
    // مش اعتمادًا على role_id === 1 (لو الرول ده اتحذف وأعيد إنشاؤه بـ id
    // مختلف، أو role_id 1 بقى لرول تاني خالص، ده كان هيدي صلاحية كاملة غلط)
    if (user.role.is_super_admin === true || user.role.name === 'SUPER_ADMIN') {
      return true;
    }
  
    try {
      // 3. فك الصلاحيات لو جيا نص
      const permissions = typeof user.role.permissions === 'string'
        ? JSON.parse(user.role.permissions)
        : user.role.permissions;
  
      // 🎯 اللعبة هنا: بنحول اسم الموديول لـ lowercase عشان نضمن التطابق
      const targetModule = module?.toLowerCase();
      
      // 🔍 سطر الديبج ده هو اللي هيعرفنا ليه بيفتح كله
      // افتح الـ Console وشوف القيمة اللي بتطلع هنا
      const isAllowed = permissions?.[targetModule]?.[action] === true;
      
      console.log(`🔐 [Check] Module: ${targetModule} | Action: ${action} | Result: ${isAllowed}`);
  
      return isAllowed;
    } catch (e) {
      console.error("❌ Permission Check Error:", e);
      return false;
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    hasPermission,
    needsSetup,
    setupLoading,
    refreshSetupStatus
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, hasPermission, needsSetup, setupLoading, refreshSetupStatus }}>
      {/* لو لسه بيحمل بنوري سبنر، لو خلص بنرندر الأبناء */}
      {!loading ? children : (
        <div className="h-screen w-screen flex flex-col items-center justify-center bg-[#f3f4f6]">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-900 mb-4"></div>
          <span className="text-blue-900 font-black italic animate-pulse uppercase tracking-widest text-[10px]">
            Initializing Security Matrix...
          </span>
        </div>
      )}
    </AuthContext.Provider>
  );
};

// 🎯 الـ Hook المطور مع صمام الأمان
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  // لو الـ context لسه null (المكون مستدعى بره الـ Provider)
  if (context === undefined || context === null) {
    // نرجع قيم وهمية "Safe Fallback" عشان السيستم ما يضربش White Screen
    return {
      user: null,
      loading: true,
      login: () => {},
      logout: () => {},
      hasPermission: () => false,
      needsSetup: false,
      setupLoading: true,
      refreshSetupStatus: () => {}
    };
  }
  
  return context;
};
